import IserbaModal from "components/shared/iserba/IserbaModal";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { MdErrorOutline } from "react-icons/md";

const GameEndScreen = ({
	title,
	score,
	text,
	levelCompleted,
	collectedItems,
	missingItems,
	onNextLevel,
	onReplay,
	correctItemsLabel = "Équipements utiles ramassés :",
	wrongItemsLabel = "Équipements inutiles ramassés :",
	missingItemsLabel = "Équipements utiles oubliés :",
}) => {
	const [displayedItems, setDisplayedItems] = useState([]);
	const [displayedScore, setDisplayedScore] = useState(0);
	const scoreRef = useRef();
	const animationStartedRef = useRef(false);

	useEffect(() => {
		animationStartedRef.current = false;
		setDisplayedItems([]);
		setDisplayedScore(0);
	}, [collectedItems]); // or a prop like `question.id` if more precise

	// Group collected items by name (ensures uniqueness)
	const groupedItems = useMemo(() => {
		return Object.values(
			collectedItems.reduce((acc, item) => {
				const key = item.value;
				if (!acc[key]) {
					acc[key] = { ...item, count: 1 };
				} else {
					acc[key].count++;
				}
				return acc;
			}, {})
		);
	}, [collectedItems]);

	useEffect(() => {
		if (animationStartedRef.current) return; // prevent re-runs
		animationStartedRef.current = true;

		const correctItems = groupedItems.filter((item) => item.isCorrect);
		const wrongItems = groupedItems.filter((item) => !item.isCorrect);

		const correctTimeout = setTimeout(() => {
			correctItems.forEach((item, index) => {
				setTimeout(() => {
					setDisplayedItems((prev) => [
						...prev,
						{ ...item, source: "correct" },
					]);
					setDisplayedScore((prev) => prev + item.count);
					if (scoreRef.current) {
						scoreRef.current.classList.remove("score-up");
						void scoreRef.current.offsetWidth;
						scoreRef.current.classList.add("score-up");
					}
				}, index * 300);
			});
		}, 500);

		const wrongTimeout = setTimeout(() => {
			wrongItems.forEach((item, index) => {
				setTimeout(() => {
					setDisplayedItems((prev) => [...prev, { ...item, source: "wrong" }]);
					setDisplayedScore((prev) => prev - item.count);
					if (scoreRef.current) {
						scoreRef.current.classList.remove("score-up");
						void scoreRef.current.offsetWidth;
						scoreRef.current.classList.add("score-up");
					}
				}, index * 300);
			});
		}, 500 + correctItems.length * 300 + 500);

		const missingTimeout = setTimeout(() => {
			(missingItems || []).forEach((item, index) => {
				setTimeout(() => {
					setDisplayedItems((prev) => [
						...prev,
						{ ...item, source: "missing", count: 1 },
					]);
				}, index * 300);
			});
		}, 500 + groupedItems.length * 300 + 500);

		return () => {
			clearTimeout(correctTimeout);
			clearTimeout(wrongTimeout);
			clearTimeout(missingTimeout);
		};
	}, [groupedItems, missingItems]);

	return (
		<>
			<IserbaModal isOpen={true}>
				<div className="flex flex-col items-center justify-center">
					<h1 className="text-gray-600 text-3xl font-bold text-center font-orbitron mb-2">
						{title}
					</h1>

					<div className="flex gap-2 items-center justify-center font-orbitron text-xl font-bold text-gray-600 mb-2">
						Score :
						<span ref={scoreRef} className="text-[#fbb554]">
							{displayedScore}
						</span>
					</div>

					{/* ✅ Correct Items */}
					{displayedItems.filter((item) => item.source === "correct").length >
						0 && (
						<div className="flex flex-col w-full mt-4">
							<h2 className="text-green-500 text-md gap-2 flex">
								<IoMdCheckmarkCircleOutline size={24} /> {correctItemsLabel}
							</h2>
							<div className="flex gap-2 flex-wrap justify-center">
								{displayedItems
									.filter((item) => item.source === "correct")
									.map((item) => (
										<div
											key={`correct-${item.value}`}
											className="smash-in relative bg-green-100 rounded-xl shadow-[6px_6px_0px_rgba(0,125,0,0.3)] flex flex-col min-w-24 w-24 h-[80px] overflow-hidden items-center justify-center"
										>
											<div
												className="bg-contain bg-no-repeat bg-center min-h-[40px] min-w-12 h-[40px] w-12 max-h-[40px] max-w-12 p-1"
												style={{
													backgroundImage: item.icon
														? `url(${item.icon})`
														: "none",
												}}
											/>
											<div className="text-xs text-gray-700 uppercase text-center w-24 whitespace-pre-wrap">
												{item.name}
											</div>
											{item.count > 1 && (
												<span className="absolute bottom-0 right-0 bg-black text-white text-xs px-1 rounded-full">
													{item.count}
												</span>
											)}
										</div>
									))}
							</div>
						</div>
					)}

					{/* ❌ Incorrect Items */}
					{displayedItems.filter((item) => item.source === "wrong").length >
						0 && (
						<div className="flex flex-col w-full mt-4">
							<h2 className="text-red-500 text-md gap-2 flex">
								<MdErrorOutline size={24} /> {wrongItemsLabel}
							</h2>
							<div className="flex gap-2 flex-wrap justify-center">
								{displayedItems
									.filter((item) => item.source === "wrong")
									.map((item) => (
										<div
											key={`wrong-${item.value}`}
											className="smash-in relative bg-red-100 rounded-xl shadow-[6px_6px_0px_rgba(255,0,0,0.3)] flex flex-col min-w-24 w-24 h-[80px] overflow-hidden items-center justify-center"
										>
											<div
												className="bg-contain bg-no-repeat bg-center min-h-[40px] min-w-12 h-[40px] w-12 max-h-[40px] max-w-12 p-1"
												style={{
													backgroundImage: item.icon
														? `url(${item.icon})`
														: "none",
												}}
											/>
											<div className="text-xs text-gray-700 uppercase text-center w-24 whitespace-pre-wrap">
												{item.name}
											</div>
											{item.count > 1 && (
												<span className="absolute bottom-0 right-0 bg-black text-white text-xs px-1 rounded-full">
													{item.count}
												</span>
											)}
										</div>
									))}
							</div>
						</div>
					)}

					{/* ❌ Missing Items */}
					{displayedItems.filter((item) => item.source === "missing").length >
						0 && (
						<div className="flex flex-col w-full mt-4">
							<h2 className="text-red-500 text-md gap-2 flex">
								<MdErrorOutline size={24} /> {missingItemsLabel}
							</h2>
							<div className="flex gap-2 flex-wrap justify-center">
								{displayedItems
									.filter((item) => item.source === "missing")
									.map((item) => (
										<div
											key={`wrong-${item.value}`}
											className="smash-in relative bg-red-100 rounded-xl shadow-[6px_6px_0px_rgba(255,0,0,0.3)] flex flex-col min-w-24 w-24 h-[80px] overflow-hidden items-center justify-center"
										>
											<div
												className="bg-contain bg-no-repeat bg-center min-h-[40px] min-w-12 h-[40px] w-12 max-h-[40px] max-w-12 p-1"
												style={{
													backgroundImage: item.icon
														? `url(${item.icon})`
														: "none",
												}}
											/>
											<div className="text-xs text-gray-700 uppercase text-center w-24 whitespace-pre-wrap">
												{item.name}
											</div>
											{item.count > 1 && (
												<span className="absolute bottom-0 right-0 bg-black text-white text-xs px-1 rounded-full">
													{item.count}
												</span>
											)}
										</div>
									))}
							</div>
						</div>
					)}

					{text && (
						<p className="text-gray-600 text-xl text-center mt-4 mb-2 font-bold">
							{text}
						</p>
					)}

					{levelCompleted ? (
						<button
							onClick={onNextLevel}
							className="mt-8 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
						>
							Niveau suivant
						</button>
					) : (
						<button
							onClick={onReplay}
							className="mt-2 rounded-full px-4 py-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300"
						>
							Rejouer
						</button>
					)}
				</div>
			</IserbaModal>
		</>
	);
};

export default GameEndScreen;
